/* eslint-disable @typescript-eslint/no-explicit-any */
// SocketContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import io from "socket.io-client";
import * as tokenStore from "../services/tokenStore";
import { Preferences } from "@capacitor/preferences";
import { HeaderNotify, User } from "./AuthContext";
import { BASE_URL } from "../services/api";
import { APP_VERSION } from "../utils/constants";

// The socket MUST follow the same blaze origin as the REST client. This previously read
// API_ENVIRONMENTS[CURRENT_ENV] directly, which bypasses BASE_URL and therefore any
// REACT_APP_BLAZE_URL override — so the socket silently pointed somewhere else than every
// other blaze call. On a device that meant `localhost`, i.e. the phone itself, and the
// socket just never connected: no error surfaced, realtime was simply absent.
const SOCKET_SERVER_URL = BASE_URL;

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  isDeleted: boolean;
  createdAt: string;
  readAt?: string;
  data?: any;
}

interface NotificationResponse {
  success: boolean;
  notifications: Notification[];
  total: number;
  unreadCount: number;
  hasMore: boolean;
  timestamp: string;
  userId: string;
}

interface SocketContextType {
  // Connection state
  connected: boolean;
  socket: any | null;
  userInitialized: boolean;
  loading: boolean;
  error: string | null;

  // Notification state
  notifications: Notification[];
  unreadCount: number;
  activityRefresh: boolean;
  dataSharingRefresh: boolean;
  reflectionsRefresh: boolean;
  // User state
  currentUser: User | null;

  // Actions
  initializeUser: (user: User) => void;
  requestNotifications: () => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  requestNotificationPermission: () => void;
  clearError: () => void;
  dailyNotification: (closeNotify?: boolean) => void;
  headerNotification: HeaderNotify;

  // Utility functions
  getNotificationTypeColor: (type: string) => string;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
  defaultUser?: any;
  timeZone?: any;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({
  children,
  defaultUser,
  timeZone,
}) => {
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState<any | null>(null);
  const [userInitialized, setUserInitialized] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(
    defaultUser || null
  );
  const [previousUser, setPreviousUser] = useState<User | null>(null);
  const [activityRefresh, setActivityRefresh] = useState(false);
  const [dataSharingRefresh, setDataSharingRefresh] = useState(false);
  const [reflectionsRefresh, setReflectionsRefresh] = useState(false);
  const [headerNotification, setHeaderNotification] = useState<any>(null);

  useEffect(() => {
    setCurrentUser((prevUser: User | null) => {
      if (prevUser) {
        setPreviousUser(prevUser);
      }
      return defaultUser || null;
    });
  }, [defaultUser]);
  // Initialize socket connection
  // Bumped on `auth:token-refreshed` so the connect effect below re-runs and re-handshakes
  // with the NEW token. socket.io v2 captures the credential at handshake time and has no
  // way to update it in place, so reconnecting is the only mechanism available.
  const [credentialEpoch, setCredentialEpoch] = useState(0);

  useEffect(() => {
    const onRefreshed = () => setCredentialEpoch((n) => n + 1);
    window.addEventListener("auth:token-refreshed", onRefreshed);
    return () => window.removeEventListener("auth:token-refreshed", onRefreshed);
  }, []);

  useEffect(() => {
    if (!currentUser?.profile_id) {
      return; // not logged in — don't open an unauthenticated socket
    }

    let socketInstance: any = null;
    let cancelled = false;

    const connect = async () => {
      try {
        // Native stores the JWT in Preferences; on web this is null and the
        // cookie (via withCredentials) authenticates instead.
        const token = await tokenStore.getAccessToken();
        if (cancelled) return;

        const options: any = {
          transports: ["polling", "websocket"],
          path: "/socket.io",
          reconnection: true,
          autoConnect: true,
          withCredentials: true,
        };
        if (token) {
          // ⚠️ QUERY, not a header — and that is a RULED DECISION, not an oversight.
          //
          // D15c specified `extraHeaders`. It does not work: `Authorization` is not a
          // CORS-safelisted header, so the WebView must preflight it, and engine.io
          // intercepts /socket.io/* at the raw HTTP server BEFORE Express — so blaze's
          // CORS config never applies there. Measured: the preflight answers 400 with no
          // `Access-Control-Allow-Headers`, the WebView correctly refuses to send the
          // header, and blaze sees an unauthenticated handshake and disconnects.
          // Passing the option through would require patching sails-hook-sockets, which
          // whitelists engine.io options and does not forward `handlePreflightRequest`.
          //
          // ⚠️ ACCEPTED RISK, weighed against the status quo rather than against an ideal:
          // production ALREADY sends a token here, and blaze's is `JWT_EXPIRY = 1d`. This
          // is a 15-minute Cognito access token — the same mechanism with ~96x less
          // exposure. The token is on EVERY polling request (Polling.prototype.uri rebuilds
          // from `this.query`), not just the handshake.
          //
          // Conditions attached to the decision:
          //   1. whatever fronts blaze MUST NOT log query strings on /socket.io/*
          //   2. revisit at the single-origin gateway — same-origin means no preflight,
          //      at which point extraHeaders works and this comment can be deleted
          options.query = { token };
        }

        socketInstance = io(SOCKET_SERVER_URL, options);
        attachListeners(socketInstance);
        setSocket(socketInstance);
      } catch (err) {
        console.error("⚠️ Socket connect failed:", err);
        setError("Connection failed. Please try again.");
      }
    };

    const attachListeners = (socketInstance: any) => {
    socketInstance.on("connect", () => {
      console.log("✅ Connected to Sails:", socketInstance.id);
      setConnected(true);
      setError(null);

      // Auto-initialize user if available
      if (currentUser) {
        initializeUserInternal(socketInstance, currentUser);
      }
    });

    socketInstance.on("disconnect", (reason: any) => {
      console.log("❌ Disconnected:", reason);
      setConnected(false);
      setUserInitialized(false);
    });

    socketInstance.on("connect_error", (error: any) => {
      console.error("⚠️ Connection error:", error);
      setError("Connection failed. Please try again.");
    });

    socketInstance.on("welcome", (data: any) => {
      console.log("🎉 Welcome:", data);
    });
    socketInstance.on("activity_refresh", () => {
      console.log("Activity refresh event received via socket");
      setActivityRefresh((prev) => !prev);
    });
    socketInstance.on("reflections_refresh", () => {
      console.log("Reflections refresh event received via socket");
      // You can add specific state updates or callbacks here if needed
      setReflectionsRefresh((prev) => !prev);
    });
    socketInstance.on("dataSharing_refresh", () => {
      setDataSharingRefresh((prev) => !prev);
    });

    // Listen for notifications list
    socketInstance.on("notifications-list", (data: NotificationResponse) => {
      console.log("📬 Notifications received:", data);
      if (data.success) {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
        setError(null);
      } else {
        setError("Failed to load notifications");
      }
      setLoading(false);
    });

    // Listen for new real-time notifications
    socketInstance.on("new-notification", (data: any) => {
      console.log("🔔 New notification:", data);
      if (data.success && data.notification) {
        setNotifications((prev) => [data.notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        showToast(data.notification);
      }
    });

    // Listen for notification read confirmations
    socketInstance.on("notification-read", (data: any) => {
      console.log("✅ Notification marked as read:", data);
      if (data.success) {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === data.notificationId
              ? { ...notif, isRead: true, readAt: data.timestamp }
              : notif
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    });

    // Listen for all notifications read confirmation
    socketInstance.on("all-notifications-read", (data: any) => {
      console.log("✅ All notifications marked as read:", data);
      if (data.success) {
        setNotifications((prev) =>
          prev.map((notif) => ({
            ...notif,
            isRead: true,
            readAt: data.timestamp,
          }))
        );
        setUnreadCount(0);
      }
    });

    // Listen for notification deletion confirmation
    socketInstance.on("notification-deleted", (data: any) => {
      console.log("🗑️ Notification deleted:", data);
      if (data.success) {
        setNotifications((prev) => {
          const deletedNotif = prev.find(
            (notif) => notif.id === data.notificationId
          );
          if (deletedNotif && !deletedNotif.isRead) {
            setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
          }
          return prev.filter((notif) => notif.id !== data.notificationId);
        });
      }
    });

    // Listen for errors
    socketInstance.on("error", (error: any) => {
      console.error("❌ any error:", error);
      setError(error.message || "An error occurred");
      setLoading(false);
    });

    // Backend rejects sockets it cannot authenticate (then disconnects them).
    // Surface that instead of failing silently.
    socketInstance.on("auth-error", (data: any) => {
      console.error("🔒 Socket auth-error:", data?.message || data);
      setError(data?.message || "Socket authentication failed");
      setConnected(false);
      setUserInitialized(false);
      // Stop reconnection — retries would reuse the same stale token and loop.
      socketInstance.disconnect();
    });
    };

    connect();

    return () => {
      cancelled = true;
      if (socketInstance) {
        socketInstance.disconnect();
        socketInstance.removeAllListeners();
      }
      setSocket(null);
      setConnected(false);
      setUserInitialized(false);
    };
    // Reconnect with fresh credentials whenever the logged-in user changes (login, logout,
    // account switch) OR the access token rotates (credentialEpoch).
    //
    // ⚠️ The epoch dependency is the fix for D15c's recorded gap: this effect used to depend
    // on profile_id ALONE, so after a refresh the socket kept the token captured at connect
    // time. It did not fail loudly — it kept working until the server next re-checked, then
    // emitted `auth-error` and permanently disconnected (see the auth-error handler below,
    // which stops reconnection). Symptom: "realtime randomly stops working."
    //
    // Cognito rotates refresh tokens with a 0-SECOND grace period, so a stale socket
    // credential is not a theoretical window.
  }, [currentUser?.profile_id, credentialEpoch]);

  // Internal user initialization
  const initializeUserInternal = useCallback(
    (socketInstance: any, user: User) => {
      socketInstance.emit(
        "initialize-user",
        {
          userId: user.profile_id,
          userName: user.first_name + " " + user.last_name,
        },
        (response: any) => {
          console.log("User initialized:", response);
          if (response.success) {
            setUserInitialized(true);
            setError(null);
            // Automatically request notifications after initialization
            requestNotificationsInternal(socketInstance, user.profile_id);
            dailyNotification();
          } else {
            console.error("Failed to initialize user:", response.error);
            setError(response.error || "Failed to initialize user");
          }
        }
      );
    },
    []
  );

  // Internal notification request
  const requestNotificationsInternal = useCallback(
    (socketInstance: any, userId: number) => {
      setLoading(true);
      setError(null);

      socketInstance.emit(
        "get-notifications",
        {
          userId: userId,
          limit: 20,
          offset: 0,
        },
        (response: any) => {
          console.log("📦 Callback response:", response);
          setNotifications(response.notifications || []);
          if (!response.success) {
            console.error("Failed to get notifications:", response.error);
            setError(response.error || "Failed to get notifications");
            setLoading(false);
          }
        }
      );
    },
    []
  );

  // Public API methods
  const initializeUser = useCallback(
    (user: User) => {
      setCurrentUser(user);
      if (socket && connected) {
        initializeUserInternal(socket, user);
      }
    },
    [socket, connected, initializeUserInternal]
  );

  const requestNotifications = useCallback(() => {
    if (!socket || !userInitialized || !currentUser) return;
    requestNotificationsInternal(socket, currentUser.profile_id);
    dailyNotification();
  }, [socket, userInitialized, currentUser, requestNotificationsInternal]);

  const markAsRead = useCallback(
    (notificationId: string) => {
      if (!socket || !userInitialized || !currentUser) return;

      socket.emit(
        "mark-notification-read",
        {
          userId: currentUser.profile_id,
          notificationId: notificationId,
        },
        (response: any) => {
          console.log("Mark as read response:", response);
          if (!response.success) {
            console.error(
              "Failed to mark notification as read:",
              response.error
            );
            setError(response.error || "Failed to mark notification as read");
          }
        }
      );
    },
    [socket, userInitialized, currentUser]
  );

  const markAllAsRead = useCallback(() => {
    if (!socket || !userInitialized || !currentUser) return;

    socket.emit(
      "mark-all-notifications-read",
      {
        userId: currentUser.profile_id,
      },
      (response: any) => {
        console.log("Mark all as read response:", response);
        if (!response.success) {
          console.error(
            "Failed to mark all notifications as read:",
            response.error
          );
          setError(
            response.error || "Failed to mark all notifications as read"
          );
        }
      }
    );
  }, [socket, userInitialized, currentUser]);

  const deleteNotification = useCallback(
    (notificationId: string) => {
      if (!socket || !userInitialized || !currentUser) return;

      socket.emit(
        "delete-notification",
        {
          userId: currentUser.profile_id,
          notificationId: notificationId,
        },
        (response: any) => {
          console.log("Delete notification response:", response);
          if (!response.success) {
            console.error("Failed to delete notification:", response.error);
            setError(response.error || "Failed to delete notification");
          }
        }
      );
    },
    [socket, userInitialized, currentUser]
  );
  const dailyNotification = useCallback(
    (closeNotify = false) => {
      console.log("📢 Requesting daily notification");

      if (!socket || !userInitialized || !currentUser) return;

      socket.emit(
        "get-header-notify",
        {
          userId: currentUser.profile_id,
          close_notify: closeNotify,
          timeZone: timeZone
            ? timeZone
            : Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        (response: any) => {
          console.log("Daily Notification Callback response:", response);
          setHeaderNotification(response);
          if (!response.success) {
            console.error("Failed to delete notification:", response.error);
            setError(response.error || "Failed to delete notification");
          }
        }
      );
      console.log("📢 Daily notification requested");
    },
    [socket, userInitialized, currentUser, timeZone]
  );
  const requestNotificationPermission = useCallback(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log("✅ Notification permission granted");
        }
      });
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Show toast notification
  const showToast = useCallback((notification: Notification) => {
    // Browser notification
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(notification.title, {
        body: notification.message,
        icon: "/notification-icon.png",
      });
    }

    // Custom toast implementation can be added here
    console.log("📱 Toast notification:", notification);
  }, []);

  // Get notification type color
  const getNotificationTypeColor = useCallback((type: string) => {
    switch (type) {
      case "success":
        return "#28a745";
      case "warning":
        return "#ffc107";
      case "error":
        return "#dc3545";
      case "info":
        return "#17a2b8";
      default:
        return "#6c757d";
    }
  }, []);

  // Re-initialize user when socket connects and user is available
  useEffect(() => {
    if (!connected || !socket || !currentUser) return;

    const userChanged =
      previousUser?.profile_id &&
      previousUser.profile_id !== currentUser.profile_id;

    if (!userInitialized || userChanged) {
      initializeUserInternal(socket, currentUser);
    }
  }, [
    connected,
    socket,
    currentUser,
    previousUser,
    userInitialized,
    initializeUserInternal,
  ]);

  const contextValue: SocketContextType = {
    // Connection state
    connected,
    socket,
    userInitialized,
    loading,
    error,
    activityRefresh,
    dataSharingRefresh,
    // Notification state
    notifications,
    unreadCount,
    reflectionsRefresh,
    headerNotification,
    // User state
    currentUser,

    // Actions
    initializeUser,
    requestNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    requestNotificationPermission,
    clearError,
    dailyNotification,

    // Utility functions
    getNotificationTypeColor,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook to use the socket context
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

// Optional: Hook for notifications only
export const useNotifications = () => {
  const context = useSocket();
  return {
    notifications: context.notifications,
    unreadCount: context.unreadCount,
    loading: context.loading,
    error: context.error,
    activityRefresh: context.activityRefresh,
    dataSharingRefresh: context.dataSharingRefresh,
    markAsRead: context.markAsRead,
    markAllAsRead: context.markAllAsRead,
    deleteNotification: context.deleteNotification,
    requestNotifications: context.requestNotifications,
    getNotificationTypeColor: context.getNotificationTypeColor,
    dailyNotification: context.dailyNotification,
    headerNotification: context.headerNotification,
  };
};

// Optional: Hook for connection status only
export const useSocketConnection = () => {
  const context = useSocket();
  return {
    connected: context.connected,
    socket: context.socket,
    userInitialized: context.userInitialized,
    currentUser: context.currentUser,
    initializeUser: context.initializeUser,
    error: context.error,
    clearError: context.clearError,
    dailyNotification: context.dailyNotification,
    headerNotification: context.headerNotification,
  };
};
